import React, { useState, useEffect } from 'react';
import {
  Box,
  Typography,
  Button,
  Card,
  CardContent,
  Grid,
  Chip,
  FormLabel,
  Select,
  Option,
  Textarea,
  Alert,
  IconButton,
  Stack
} from '@mui/joy';
import {
  Modal,
  ModalDialog,
  ModalClose
} from '@mui/joy';
import { Add, Edit, Delete, Work } from '@mui/icons-material';
import { PreferenceService } from '@/services/preferenceService';
import { shiftTypeService } from '@/services/shiftTypeService';
import { useAuth } from '@/context/AuthContext';
import {
  ShiftTypePreference,
  CreateShiftTypePreferenceData,
  UpdateShiftTypePreferenceData,
  SHIFT_TYPE_PREFERENCE_TYPES,
  EXPERIENCE_LEVEL_OPTIONS,
  PRIORITY_OPTIONS
} from '@/types/preference';
import { ShiftType } from '@/types/shiftType';

interface ShiftTypePreferencesTabProps {
  preferences: ShiftTypePreference[];
  onUpdate: () => void;
}

const ShiftTypePreferencesTab: React.FC<ShiftTypePreferencesTabProps> = ({
  preferences,
  onUpdate
}) => {
  const { user } = useAuth();
  const [openDialog, setOpenDialog] = useState(false);
  const [editingPreference, setEditingPreference] = useState<ShiftTypePreference | null>(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [shiftTypes, setShiftTypes] = useState<ShiftType[]>([]);

  const [formData, setFormData] = useState<CreateShiftTypePreferenceData>({
    employee_id: user?.id || 0,
    shift_type_id: 0,
    preference_type: 'acceptable',
    experience_level: 'intermediate',
    notes: '',
    priority: 'medium'
  });

  useEffect(() => {
    loadShiftTypes();
  }, []);

  const loadShiftTypes = async () => {
    try {
      const data = await shiftTypeService.getAllShiftTypes();
      setShiftTypes(data);
    } catch (err) {
      console.error('Error loading shift types:', err);
    }
  };

  const handleOpenDialog = (preference?: ShiftTypePreference) => {
    if (preference) {
      setEditingPreference(preference);
      setFormData({
        employee_id: preference.employee_id,
        shift_type_id: preference.shift_type_id,
        preference_type: preference.preference_type,
        experience_level: preference.experience_level,
        notes: preference.notes || '',
        priority: preference.priority
      });
    } else {
      setEditingPreference(null);
      setFormData({
        employee_id: user?.id || 0,
        shift_type_id: 0,
        preference_type: 'acceptable',
        experience_level: 'intermediate',
        notes: '',
        priority: 'medium'
      });
    }
    setOpenDialog(true);
    setError(null);
  };

  const handleCloseDialog = () => {
    setOpenDialog(false);
    setEditingPreference(null);
    setError(null);
  };

  const handleSubmit = async () => {
    try {
      setLoading(true);
      setError(null);

      const errors = PreferenceService.validateShiftTypePreference(formData);
      if (errors.length > 0) {
        setError(errors.join(', '));
        return;
      }

      if (editingPreference) {
        await PreferenceService.updateShiftTypePreference(editingPreference.id, formData);
      } else {
        await PreferenceService.createShiftTypePreference(formData);
      }

      handleCloseDialog();
      onUpdate();
    } catch (err) {
      console.error('Error saving shift type preference:', err);
      setError('Failed to save preference. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  const handleDelete = async (preference: ShiftTypePreference) => {
    if (window.confirm('Are you sure you want to delete this preference?')) {
      try {
        await PreferenceService.deleteShiftTypePreference(preference.id);
        onUpdate();
      } catch (err) {
        console.error('Error deleting preference:', err);
        setError('Failed to delete preference. Please try again.');
      }
    }
  };

  const getPreferenceTypeColor = (type: string) => {
    switch (type) {
      case 'preferred': return 'success';
      case 'acceptable': return 'warning';
      case 'avoid': return 'danger';
      default: return 'neutral';
    }
  };

  const getPriorityColor = (priority: string) => {
    switch (priority) {
      case 'high': return 'danger';
      case 'medium': return 'warning';
      case 'low': return 'success';
      default: return 'neutral';
    }
  };

  const getShiftTypeName = (shiftTypeId: number) => {
    const shiftType = shiftTypes.find(st => st.id === shiftTypeId);
    return shiftType?.name || 'Unknown Shift Type';
  };

  return (
    <Box>
      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 3 }}>
        <Typography level="h4">
          Shift Type Preferences
        </Typography>
        <Button
          startDecorator={<Add />}
          onClick={() => handleOpenDialog()}
          variant="solid"
        >
          Add Shift Type Preference
        </Button>
      </Box>

      {error && (
        <Alert color="danger" sx={{ mb: 2 }}>
          {error}
        </Alert>
      )}

      {preferences.length === 0 ? (
        <Card variant="soft">
          <CardContent>
            <Box sx={{ textAlign: 'center', py: 4 }}>
              <Work sx={{ fontSize: 48, color: 'neutral.400', mb: 2 }} />
              <Typography level="title-sm" color="neutral">
                No shift type preferences set
              </Typography>
              <Typography level="body-sm" color="neutral" sx={{ mb: 2 }}>
                Add shift type preferences to indicate which shifts you prefer or want to avoid.
              </Typography>
              <Button onClick={() => handleOpenDialog()}>
                Add Your First Shift Type Preference
              </Button>
            </Box>
          </CardContent>
        </Card>
      ) : (
        <Grid container spacing={2}>
          {preferences.map((preference) => (
            <Grid xs={12} md={6} lg={4} key={preference.id}>
              <Card variant="outlined">
                <CardContent>
                  <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', mb: 2 }}>
                    <Box>
                      <Typography level="title-sm">
                        {getShiftTypeName(preference.shift_type_id)}
                      </Typography>
                      <Typography level="body-sm" color="neutral">
                        Experience: {preference.experience_level}
                      </Typography>
                    </Box>
                    <Box sx={{ display: 'flex', gap: 1 }}>
                      <IconButton
                        size="sm"
                        variant="plain"
                        onClick={() => handleOpenDialog(preference)}
                      >
                        <Edit />
                      </IconButton>
                      <IconButton
                        size="sm"
                        variant="plain"
                        color="danger"
                        onClick={() => handleDelete(preference)}
                      >
                        <Delete />
                      </IconButton>
                    </Box>
                  </Box>

                  <Stack direction="row" spacing={1} sx={{ mb: 2 }}>
                    <Chip
                      size="sm"
                      color={getPreferenceTypeColor(preference.preference_type)}
                    >
                      {preference.preference_type}
                    </Chip>
                    <Chip
                      size="sm"
                      color={getPriorityColor(preference.priority)}
                      variant="soft"
                    >
                      {preference.priority}
                    </Chip>
                  </Stack>

                  {preference.notes && (
                    <Typography level="body-sm" sx={{ mb: 1 }}>
                      <strong>Notes:</strong> {preference.notes}
                    </Typography>
                  )}
                </CardContent>
              </Card>
            </Grid>
          ))}
        </Grid>
      )}

      {/* Add/Edit Modal */}
      <Modal open={openDialog} onClose={handleCloseDialog}>
        <ModalDialog size="sm">
          <ModalClose />
          <Typography level="title-sm" component="h2">
            {editingPreference ? 'Edit Shift Type Preference' : 'Add Shift Type Preference'}
          </Typography>
          <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2, pt: 1 }}>
            <Box>
              <FormLabel>Shift Type</FormLabel>
              <Select
                value={formData.shift_type_id}
                onChange={(e, value) => setFormData({ ...formData, shift_type_id: value || 0 })}
              >
                {shiftTypes.map((shiftType) => (
                  <Option key={shiftType.id} value={shiftType.id}>
                    {shiftType.name}
                  </Option>
                ))}
              </Select>
            </Box>

            <Box>
              <FormLabel>Preference Type</FormLabel>
              <Select
                value={formData.preference_type}
                onChange={(e, value) => setFormData({ ...formData, preference_type: value || 'acceptable' })}
              >
                {SHIFT_TYPE_PREFERENCE_TYPES.map((type) => (
                  <Option key={type.value} value={type.value}>
                    {type.label}
                  </Option>
                ))}
              </Select>
            </Box>

            <Box>
              <FormLabel>Experience Level</FormLabel>
              <Select
                value={formData.experience_level}
                onChange={(e, value) => setFormData({ ...formData, experience_level: value || 'intermediate' })}
              >
                {EXPERIENCE_LEVEL_OPTIONS.map((level) => (
                  <Option key={level.value} value={level.value}>
                    {level.label}
                  </Option>
                ))}
              </Select>
            </Box>

            <Box>
              <FormLabel>Priority</FormLabel>
              <Select
                value={formData.priority}
                onChange={(e, value) => setFormData({ ...formData, priority: value || 'medium' })}
              >
                {PRIORITY_OPTIONS.map((priority) => (
                  <Option key={priority.value} value={priority.value}>
                    {priority.label}
                  </Option>
                ))}
              </Select>
            </Box>

            <Box>
              <FormLabel>Notes (Optional)</FormLabel>
              <Textarea
                minRows={2}
                value={formData.notes}
                onChange={(e) => setFormData({ ...formData, notes: e.target.value })}
                placeholder="Add any notes about this shift type preference..."
              />
            </Box>
          </Box>
          <Box sx={{ display: 'flex', gap: 1, justifyContent: 'flex-end', pt: 2 }}>
            <Button variant="plain" onClick={handleCloseDialog}>
              Cancel
            </Button>
            <Button
              onClick={handleSubmit}
              loading={loading}
              disabled={loading}
            >
              {editingPreference ? 'Update' : 'Create'}
            </Button>
          </Box>
        </ModalDialog>
      </Modal>
    </Box>
  );
};

export default ShiftTypePreferencesTab; 